import React, { useState, useEffect } from 'react';
import {
  PowerService,
  ForecastType,
  GermanTSO,
  SolarForecastResponse,
  WindOnshoreForecastResponse,
  WindOffshoreForecastResponse,
  LoadForecastResponse,
} from '../../../services/powerService';
import { supabase } from '../../auth/AuthProvider';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { format, sub } from 'date-fns';
import ForecastChart from './ForecastChart';
import ForecastSummary from './ForecastSummary';

interface PowerForecastsProps {
  defaultCountryCode?: string;
}

const ForecastTypes: { value: ForecastType; label: string }[] = [
  { value: 'solar', label: 'Solar' },
  { value: 'wind_onshore', label: 'Wind Onshore' },
  { value: 'wind_offshore', label: 'Wind Offshore' },
  { value: 'load', label: 'Load' },
];

const GermanTSOs: { value: GermanTSO | ''; label: string }[] = [
  { value: '', label: 'All TSOs' },
  { value: 'TransnetBW', label: 'TransnetBW' },
  { value: '50Hertz', label: '50Hertz' },
  { value: 'TenneT', label: 'TenneT' },
  { value: 'Amprion', label: 'Amprion' },
];

const timePeriods = [
  { value: 'latest', label: 'Latest data (56 hours)' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

// ------------------------------------------------------------------
// Data sources – "tso" is the existing CSV/ENTSO-E style data, "jua" is
// the Supabase backed forecast provided by Jua.ai.
// ------------------------------------------------------------------
const DataSources: { value: 'tso' | 'jua'; label: string }[] = [
  { value: 'tso', label: 'TSO forecast' },
  { value: 'jua', label: 'Jua forecast' },
];

const PowerForecasts: React.FC<PowerForecastsProps> = ({ defaultCountryCode = 'DE' }) => {
  const [countryOptions, setCountryOptions] = useState<Record<string, string> | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountryCode);
  const [selectedForecastType, setSelectedForecastType] = useState<ForecastType>('solar');
  const [selectedTSO, setSelectedTSO] = useState<GermanTSO | ''>('');
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('latest');
  const [selectedDataSource, setSelectedDataSource] = useState<'tso' | 'jua'>('tso');
  const [forecastData, setForecastData] = useState<
    | SolarForecastResponse
    | WindOnshoreForecastResponse
    | WindOffshoreForecastResponse
    | LoadForecastResponse
    | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);
        const countriesResponse = await powerService.getPowerForecastCountries();

        if (countriesResponse.error || !countriesResponse.data) {
          throw new Error(countriesResponse.error || 'Failed to fetch countries');
        }

        setCountryOptions(countriesResponse.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchForecastData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);

        // ------------------------------------------------------------
        // Branch based on selected data source
        // ------------------------------------------------------------
        let forecastResponse;

        if (selectedDataSource === 'jua') {
          if (!['wind_onshore', 'solar'].includes(selectedForecastType)) {
            throw new Error('Jua forecast currently available for wind onshore and solar only');
          }

          // 1️⃣ Fetch Jua forecast (Supabase)
          const juaResponse =
            selectedForecastType === 'wind_onshore'
              ? await powerService.getSupabaseWindOnshoreForecast()
              : await powerService.getSupabaseSolarForecast();

          if (juaResponse.error || !juaResponse.data) {
            throw new Error(juaResponse.error || 'Failed to fetch Jua forecast');
          }

          // 2️⃣ Fetch TSO forecast to obtain *actual* values for the same period
          const tsoResp = await powerService.getPowerForecasts({
            country_code: selectedCountry,
            forecast_type: selectedForecastType,
            latest: true,
          });

          if (tsoResp.error || !tsoResp.data) {
            throw new Error(tsoResp.error || 'Failed to fetch TSO actual values');
          }

          // 3️⃣ Merge actuals from TSO into the Jua dataset
          // Build a map normalised by epoch milliseconds to avoid format / TZ mismatches
          const actualMap = new Map<number, number>();
          tsoResp.data.data.forEach((d) => {
            if (d.actual_value !== undefined && d.actual_value !== null) {
              const epoch = new Date(d.timestamp).getTime();
              actualMap.set(epoch, d.actual_value);
            }
          });

          const mergedData = juaResponse.data.data.map((d) => {
            const epoch = new Date(d.timestamp).getTime();
            return {
              ...d,
              actual_value: actualMap.get(epoch) ?? d.actual_value,
            };
          });

          forecastResponse = {
            data: {
              ...juaResponse.data,
              data: mergedData,
            },
            error: null,
          } as const;
        } else {
          const params: any = {
            country_code: selectedCountry,
            forecast_type: selectedForecastType,
            tso: selectedTSO as GermanTSO | undefined,
          };

          // Remove tso if empty
          if (!params.tso) {
            delete params.tso;
          }

          // Use either 'latest' option or calculate start_time and end_time
          if (selectedTimePeriod === 'latest') {
            params.latest = true;
          } else {
            // Calculate time period
            let startDate = new Date();
            switch (selectedTimePeriod) {
              case '7d':
                startDate = sub(new Date(), { days: 7 });
                break;
              case '30d':
                startDate = sub(new Date(), { days: 30 });
                break;
              default: // 24h
                startDate = sub(new Date(), { days: 1 });
                break;
            }

            params.start_time = format(startDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");
            params.end_time = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'");
          }

          forecastResponse = await powerService.getPowerForecasts(params);
        }

        if (forecastResponse.error || !forecastResponse.data) {
          throw new Error(forecastResponse.error || 'Failed to fetch power forecasts');
        }

        setForecastData(forecastResponse.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchForecastData();
  }, [selectedCountry, selectedForecastType, selectedTSO, selectedTimePeriod, selectedDataSource]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
  };

  const handleForecastTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedForecastType(e.target.value as ForecastType);
  };

  const handleTSOChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTSO(e.target.value as GermanTSO | '');
  };

  const handleTimePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTimePeriod(e.target.value);
  };

  const handleDataSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDataSource(e.target.value as 'tso' | 'jua');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Power Forecasts
        </h2>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Country
            </label>
            <select
              id="country"
              value={selectedCountry}
              onChange={handleCountryChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {countryOptions &&
                Object.entries(countryOptions).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
            </select>
          </div>

          {/* Forecast Type */}
          <div>
            <label
              htmlFor="forecastType"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Forecast Type
            </label>
            <select
              id="forecastType"
              value={selectedForecastType}
              onChange={handleForecastTypeChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {ForecastTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Data Source */}
          <div>
            <label
              htmlFor="dataSource"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Data Source
            </label>
            <select
              id="dataSource"
              value={selectedDataSource}
              onChange={handleDataSourceChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {DataSources.map((src) => (
                <option key={src.value} value={src.value}>
                  {src.label}
                </option>
              ))}
            </select>
          </div>

          {/* TSO selector (disabled for non-DE or Jua source) */}
          <div>
            <label
              htmlFor="tso"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              TSO (Germany only)
            </label>
            <select
              id="tso"
              value={selectedTSO}
              onChange={handleTSOChange}
              disabled={selectedCountry !== 'DE' || selectedDataSource === 'jua'}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              {GermanTSOs.map((tso) => (
                <option key={tso.value || 'all'} value={tso.value}>
                  {tso.label}
                </option>
              ))}
            </select>
          </div>

          {/* Time period selector (disabled for Jua source) */}
          <div>
            <label
              htmlFor="timePeriod"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Time Period
            </label>
            <select
              id="timePeriod"
              value={selectedTimePeriod}
              onChange={handleTimePeriodChange}
              disabled={selectedDataSource === 'jua'}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              {timePeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Chart Area */}
      <div className="border border-gray-300 dark:border-gray-700 rounded-md">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-500">{error}</p>
          </div>
        ) : !forecastData || forecastData.data.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400">
              No forecast data available for the selected filters.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 p-4">
              <ForecastChart forecastData={forecastData} forecastType={selectedForecastType} />
              <ForecastSummary forecastData={forecastData} forecastType={selectedForecastType} />
            </div>
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-300 dark:border-gray-700">
              Forecast data for {selectedForecastType.replace('_', ' ')} in{' '}
              {countryOptions?.[selectedCountry] || selectedCountry}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PowerForecasts;
